/**
 * The pure row logic both Injuries tabs (game detail and team profile) share.
 * ISSUE-984. Both INJURIES_NONE_LABEL and INJURIES_NO_REPORT_LABEL are
 * planner authored strings, not derived.
 */
import type { typePlayerSearch } from "../../routeState/playerSearch";
import {
  initialsFromPersonName,
  PLAYER_INJURY_STATUS_LABELS,
  type typePlayerInjuryStatus,
} from "../playerProfile/playerProfileView";

export type typeInjuryListEntry = {
  playerId?: string | null;
  name: string;
  position?: string | null;
  status: typePlayerInjuryStatus;
  note?: string | null;
  /** A media URL from the server, or null for the silhouette (ISSUE-983). */
  headshotUrl?: string | null;
};

export type typeInjuryRowModel = {
  key: string;
  /** The trimmed player id, or null when the entry has none (ISSUE-1168). */
  playerId: string | null;
  status: typePlayerInjuryStatus;
  name: string;
  initials: string;
  headshotUrl: string | null;
  position: string | null;
  statusLine: string;
};

export const INJURIES_NONE_LABEL = "No reported injuries";
export const INJURIES_NO_REPORT_LABEL = "No injury report available";

const escapeRegExp = (rawValue: string): string =>
  rawValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** "I.L.: I.L. - Chest" reads "I.L.: Chest" and "I.L.: I.L." reads "I.L.": the ingest wrote the vendor status twice before ISSUE-1167. Any other note is returned unchanged. */
function dropRepeatedStatusToken(note: string): string {
  const colon = note.indexOf(":");
  if (colon === -1) return note;

  const head = note.slice(0, colon).trim();
  if (head === "") return note;

  const rest = note.slice(colon + 1).trim();
  const tokenPattern = escapeRegExp(head).replace(/\s+/g, "\\s+");
  const leading = new RegExp(
    `^${tokenPattern}(?:\\s*:\\s*|\\s+-\\s*|\\s*$)`,
    "i",
  );

  if (!leading.test(rest)) return note;

  const detail = rest.replace(leading, "").trim();
  return detail === "" ? head : `${head}: ${detail}`;
}

/** "Out" or "Out, Knee". A note that repeats its vendor status reads it once: "I.L.: I.L. - Chest" gives "Out, I.L.: Chest" (ISSUE-1167). */
export function injuryStatusLine(
  status: typePlayerInjuryStatus,
  note: string | null | undefined,
): string {
  const label = PLAYER_INJURY_STATUS_LABELS[status];
  const trimmedNote = dropRepeatedStatusToken(note?.trim() ?? "");
  return trimmedNote === "" ? label : `${label}, ${trimmedNote}`;
}

/** Server order is kept. */
export function toInjuryRowModels(
  entries: readonly typeInjuryListEntry[],
): typeInjuryRowModel[] {
  return entries.map((entry, index) => {
    const playerId = entry.playerId ?? null;
    const position = entry.position ?? null;
    const trimmedPlayerId = entry.playerId?.trim() ?? "";
    return {
      playerId: trimmedPlayerId === "" ? null : trimmedPlayerId,
      status: entry.status,
      key:
        playerId !== null && playerId !== ""
          ? playerId
          : `${entry.name}-${String(index)}`,
      name: entry.name,
      initials: initialsFromPersonName(entry.name),
      headshotUrl: entry.headshotUrl ?? null,
      position: position === "" ? null : position,
      statusLine: injuryStatusLine(entry.status, entry.note),
    };
  });
}

export type typeInjuryListTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
};

/** The /scores/player params for one injury row, or null when the row has no playerId. ISSUE-1168. */
export function injuryRowPlayerSearch(
  row: typeInjuryRowModel,
  team: typeInjuryListTeam,
): typePlayerSearch | null {
  if (row.playerId === null) return null;
  return {
    playerId: row.playerId,
    name: row.name,
    teamId: team.teamId,
    teamName: team.name,
    teamAbbreviation: team.abbreviation,
    injuryStatus: row.status,
    ...(row.position === null ? {} : { position: row.position }),
  };
}
